import { create } from "zustand";

import { queryClient } from "../lib/queryClient";

/**
 * Whether this browser holds a session.
 *
 * Deliberately *not* persisted, unlike every other store here. The session
 * lives in an HttpOnly cookie that only the server can read or revoke; a copy
 * of "logged in" in localStorage could only ever disagree with it, and would
 * disagree in the direction of showing the app to someone who no longer has a
 * session. The server is asked once on boot and believed thereafter.
 */
export type AuthStatus = "checking" | "authenticated" | "anonymous";

export type LoginResult = { ok: true } | { ok: false; message: string };

interface AuthState {
  status: AuthStatus;
  /** False when the server has no passphrase configured — nobody can log in. */
  configured: boolean;
  check: () => Promise<void>;
  login: (passphrase: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  /** Called from the fetch layer when the API rejects a request as 401. */
  markAnonymous: () => void;
}

async function postJson(path: string, body?: unknown) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { res, data } as { res: Response; data: Record<string, unknown> };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "checking",
  configured: true,

  check: async () => {
    try {
      const res = await fetch("/api/auth/session");
      const data = (await res.json()) as {
        authenticated: boolean;
        configured: boolean;
      };
      set({
        status: data.authenticated ? "authenticated" : "anonymous",
        configured: data.configured,
      });
    } catch {
      // A server that cannot be reached is not a session. Failing closed keeps
      // the app from mounting against an API that will 401 everything anyway.
      set({ status: "anonymous" });
    }
  },

  login: async (passphrase) => {
    const { res, data } = await postJson("/api/auth/login", { passphrase });
    if (res.ok) {
      set({ status: "authenticated" });
      return { ok: true };
    }
    if (res.status === 429) {
      const wait = Number(data.retry_after ?? 0);
      const mins = Math.max(1, Math.ceil(wait / 60));
      return {
        ok: false,
        message: `Too many attempts. Try again in ${mins} min.`,
      };
    }
    const left = data.attempts_left;
    return {
      ok: false,
      message:
        typeof left === "number" && left > 0
          ? `Incorrect passphrase — ${left} attempt${left === 1 ? "" : "s"} left.`
          : "Incorrect passphrase.",
    };
  },

  logout: async () => {
    // Order matters: drop the local view of the session first, so a failed
    // request cannot leave the app rendering data it can no longer refresh.
    get().markAnonymous();
    await postJson("/api/auth/logout").catch(() => undefined);
  },

  markAnonymous: () => {
    if (get().status === "anonymous") return;
    set({ status: "anonymous" });
    queryClient.clear();
  },
}));
